import React, { useState } from "react";
import Editor from "@monaco-editor/react";
import type { CodeGeneratorProps } from "./types";
import { SDK_CONFIG, THEMES } from "./constants";

export const CodeGenerator: React.FC<CodeGeneratorProps> = ({
  config,
  theme,
  customTheme,
  sdk,
  onOpenPreorder,
}) => {
  const [copied, setCopied] = useState(false);

  const generateCode = () => {
    const customThemeCode =
      theme === "custom" && customTheme
        ? `
// Кастомная тема с цветами
const customTheme = ${JSON.stringify({ theme: "custom", colors: customTheme }, null, 2)};

// Установка кастомной темы
window.preorder.setTheme(customTheme);`
        : "";

    const themeCode =
      theme && theme !== "light" && theme !== "custom"
        ? `window.preorder.setTheme("${theme}");`
        : theme === "light"
          ? "// window.preorder.setTheme('light'); // тема по умолчанию"
          : "// window.preorder.setTheme('light'); // тема по умолчанию";

    const orderIdSection = config.orderId ? `
// Редактирование существующего заказа
// config.orderId = '${config.orderId}';` : '';

    return `// Подключение Preorder SDK
// <script src="${SDK_CONFIG.PROD_URL}"></script>
// import preorder from './preorder.sdk.umd.js'

// Конфигурация SDK
const config = ${JSON.stringify(config, null, 2)};${customThemeCode}

// Установка темы (опционально)
${themeCode}${orderIdSection}

// Открытие предзаказа
async function openPreorder() {
  try {
    const result = await window.preorder.open(config);
    
    if (result && result.orderId) {
      console.log('✅ Заказ создан:', result);
      alert(\`Заказ №\${result.orderId} создан! Сумма: \${result.total} \${result.currency}\`);
    } else if (result && result.code) {
      console.warn('⚠️ Операция завершилась с кодом:', result.code);
      
      if (result.code === 'USER_CANCELLED') {
        console.log('👤 Пользователь отменил заказ');
      } else {
        console.error('❌ Ошибка при создании заказа:', result);
        alert(\`Произошла ошибка: \${result.message || 'Неизвестная ошибка'}\`);
      }
    }
  } catch (error) {
    console.error('❌ Критическая ошибка при открытии предзаказа:', error);
    alert('Не удалось открыть окно предзаказа');
  }
}

// Использование
// openPreorder();

// Управление кодом авторизации
function setAuthCode(code) {
  window.preorder.setAuthCode(code);
  console.log('🔐 Код авторизации установлен:', code);
}

function getAuthCode() {
  const code = window.preorder.getAuthCode();
  console.log('🔍 Текущий код авторизации:', code);
  return code;
}

function clearAuthCode() {
  window.preorder.clearAuthCode();
  console.log('🗑️ Код авторизации очищен');
}

function forceCloseModal() {
  if (window.preorder.isOpen) {
    window.preorder.close();
    console.log('❌ Модальное окно принудительно закрыто');
  }
}

function resetSDK() {
  try {
    window.preorder.reset();
    console.log('🔄 SDK сброшен к начальному состоянию');
  } catch (error) {
    console.error('❌ Ошибка при сбросе SDK:', error);
  }
}`;
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(generateCode());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Ошибка копирования:", error);
      // Fallback для старых браузеров
      const textArea = document.createElement("textarea");
      textArea.value = generateCode();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenPreorder = async () => {
    if (!sdk) {
      console.error("SDK not loaded");
      return;
    }

    try {
      // Применяем тему если выбрана
      if (theme && theme !== "light") {
        if (theme === "custom" && customTheme) {
          sdk.setTheme({ theme: "custom", colors: customTheme });
        } else {
          sdk.setTheme(theme);
        }
      }

      // Открываем предзаказ с конфигурацией
      const result = await sdk.open(config);

      if (result && result.orderId) {
        console.log("✅ Заказ создан:", result);
        // Вызываем callback для обновления состояния в родительском компоненте
        if (onOpenPreorder) {
          onOpenPreorder(result);
        }
      } else if (result && result.code) {
        console.warn("⚠️ Операция завершилась с кодом:", result.code);

        if (result.code === "USER_CANCELLED") {
          console.log("👤 Пользователь отменил заказ");
        } else {
          console.error("❌ Ошибка при создании заказа:", result);
        }
      }
    } catch (error) {
      console.error("❌ Критическая ошибка при открытии предзаказа:", error);
    }
  };

  return (
    <div className="code-generator">
      <div className="code-header">
        <h3>Результат</h3>
        <div className="code-header-buttons">
          <button
            className="open-preorder-button"
            onClick={handleOpenPreorder}
            disabled={!sdk}
            title="Открыть окно предзаказа с текущими настройками"
          >
            🚀 Открыть предзаказ
          </button>
          <button
            className={`copy-button ${copied ? "copied" : ""}`}
            onClick={handleCopyCode}
          >
            {copied ? "✅ Скопировано!" : "📋 Копировать код"}
          </button>
        </div>
      </div>

      <div className="code-editor">
        <Editor
          height="600px"
          theme="vs-dark"
          options={{
            readOnly: true,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 14,
            lineNumbers: "on",
            wordWrap: "on",
          }}
          defaultLanguage="javascript"
          value={generateCode()}
        />
      </div>

      <div className="code-info">
        <p>
          <strong>💡 Совет:</strong> Скопируйте код и вставьте в ваше
          приложение. Убедитесь, что SDK подключен перед использованием.
        </p>
        <p>
          <strong>🎨 Тема:</strong> Текущая тема:{" "}
          <code>{theme || "light"}</code>
          {theme === "custom" && (
            <span>
              {" "}
              (включает кастомные цвета: primary:{" "}
              <span className="color-preview">
                <span
                  className="color-square"
                  style={{ backgroundColor: THEMES.custom.colors.primary.main }}
                ></span>
                {THEMES.custom.colors.primary.main}
              </span>
              , background:{" "}
              <span className="color-preview">
                <span
                  className="color-square"
                  style={{
                    backgroundColor: THEMES.custom.colors.background.default,
                  }}
                ></span>
                {THEMES.custom.colors.background.default}
              </span>
              )
            </span>
          )}
        </p>
      </div>
    </div>
  );
};
